import { useCallback, useEffect, useState } from 'react';

import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import PropTypes from 'prop-types';

import { useButtonPressedCallback, useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';

import CarouselContainer from './CarouselContainer';
import { CarouselContextProvider } from './CarouselContext';

function Carousel({
  autoplayDurationInSeconds = 15,
  children,
  totalSlides = 1,
  eventingProperties = {
    bannerType: 'promo',
    elementName: 'dashboard_carousel',
    elementValue: []
  }
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ playOnInit: true, delay: autoplayDurationInSeconds * 1000 })
  ]);

  const [progressState, setProgressState] = useState({
    autoplayDuration: autoplayDurationInSeconds * 1000,
    currentSlide: 0,
    isAutoplay: true,
    totalSlides
  });

  const handleBannerDisplayed = useClickstreamCallback('bannerDisplayed', eventingProperties);

  // Track banner display once when carousel is rendered
  useEffect(() => {
    handleBannerDisplayed();
  }, [handleBannerDisplayed]);

  const updateProgress = useCallback((api, isAutoplay) => {
    setProgressState((currentState) => ({
      ...currentState,
      currentSlide: api.selectedScrollSnap(),
      isAutoplay
    }));
  }, []);

  useEffect(() => {
    setProgressState((currentState) => ({
      ...currentState,
      totalSlides
    }));
  }, [totalSlides]);

  useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    emblaApi
      .on('autoplay:timerset', (api) => updateProgress(api, true))
      .on('autoplay:timerstopped', (api) => updateProgress(api, false));
  }, [emblaApi, updateProgress]);

  const trackPreviousButtonClick = useButtonPressedCallback({
    elementName: 'carousel_change',
    elementText: 'previous'
  });

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    trackPreviousButtonClick();
    emblaApi.scrollPrev();
    emblaApi.plugins().autoplay.play();
  }, [emblaApi, trackPreviousButtonClick]);

  const trackNextButtonClick = useButtonPressedCallback({
    elementName: 'carousel_change',
    elementText: 'next'
  });

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    trackNextButtonClick();
    emblaApi.scrollNext();
    emblaApi.plugins().autoplay.play();
  }, [emblaApi, trackNextButtonClick]);

  return (
    <CarouselContextProvider emblaApi={emblaApi}>
      <CarouselContainer
        emblaRef={emblaRef}
        handlePrevButtonOnClick={onPrevButtonClick}
        handleNextButtonOnClick={onNextButtonClick}
        progressState={progressState}
      >
        {children}
      </CarouselContainer>
    </CarouselContextProvider>
  );
}

Carousel.propTypes = {
  autoplayDurationInSeconds: PropTypes.number,
  totalSlides: PropTypes.number,
  eventingProperties: PropTypes.shape({
    bannerType: PropTypes.string,
    elementName: PropTypes.string,
    elementValue: PropTypes.arrayOf(PropTypes.string)
  })
};

export default Carousel;
